// @givmo/app-admin — Tax documents (07-1.6 list/download + 07-3b gap ③ generate). The company
// annual-report PDF surface.
//
// EP-DES2 Wave 0 moved this from its own `/documents` page to the "Tax documents" SECTION of
// `/reports` (spec §1: "Documents" could not host exports and a recap without becoming a misnomer).
// `/documents` redirects to `/reports`; every behavior, wire call, and `documents-*` testid below is
// unchanged — only the outer chrome (page → section) and the heading moved.
//
// A company admin LISTS, GENERATES, and DOWNLOADS their company's annual-report PDF artifacts. The
// Annual Reports program shipped the backend (prod, 2026-06-17); we consume the generated client
// with NO fork, no backend work. GENERATE absorbs the Flutter webapp's Reporting screen — the
// `/reporting` placeholder route is retired and redirects here.
//
// Wiring:
//   - LIST     → `donationsListCompanyStatsReports` (GET /users/me/company/donations/reports) via the
//                generated `donationsListCompanyStatsReportsOptions` TanStack-Query factory. The
//                response is `Array<StatisticsReportOut>`; the shared `extractList` decodes it tolerantly
//                (bare array OR the un-unwrapped `{data}` envelope). Server-scoped to the caller's
//                `admin_company`; a non-company-admin gets 403 (surfaced as a graceful error state).
//   - GENERATE → a header action opening `GenerateReportDialog` (year picker → POST
//                /users/me/company/donations/reports). The dialog owns the mutation + the
//                list-invalidation on success; see its header for the accepted/emailed semantics.
//   - DOWNLOAD → `donationsDownloadStatsReport` (GET /users/me/donations/reports/{report_id}/download)
//                with `parseAs: 'blob'` + `responseStyle: 'fields'` (so we get the raw `Response` and
//                can read the Content-Disposition filename via the package's `readBinaryMeta`), then a
//                client-side Blob save (object-URL <a download> click + revoke). The backend
//                404-collapses missing / not-owned / not-yet-COMPLETED into one not-found, so the
//                Download button is ENABLED ONLY when `status === 'Completed'` (disabled with a "Not
//                ready" hint otherwise). Download errors surface via `toast.error`.
//
// RBAC: NO client Owner-gate — any company admin can view their company's reports; the backend
// enforces `admin_company`. A 403 (shouldn't happen for a logged-in company admin) renders gracefully.
//
// The root keeps `data-testid="page-documents"` (the route-guard / e2e contract) even as a section.

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  donationsGenerateCompanyStatsReportMutation,
  donationsListCompanyStatsReportsOptions,
  donationsListCompanyStatsReportsQueryKey,
} from '@givmo/api-client/query';
import { sdk, readBinaryMeta, isGivmoError, type StatisticsReportOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { Download, FilePlus2, FileText, RotateCcw } from 'lucide-react';
import { extractList } from '@givmo/api-client/list';
import {
  buildRetryReportBody,
  isReportDownloadable,
  isReportRetryable,
  reportPeriodLabel,
  reportFilename,
} from './documents-decode';
import { GenerateReportDialog } from './generate-report-dialog';
// EP-WEB7: the download plumbing moved to the reports feature's csv-export module — one home per app.
import { asSaveableBlob, binaryDownloadOptions, blobSave } from '../reports/csv-export';

const PREFIX = 'documents';

/** SaaS Badge tone for a report status. */
function statusBadgeTone(status: string): 'success' | 'info' | 'danger' | 'neutral' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Pending':
    case 'In Progress':
      return 'info';
    case 'Failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function TaxDocumentsSection() {
  const queryClient = useQueryClient();
  // The id of the report whose download is in flight (so we can show a per-row busy state and prevent
  // double-clicks), or null when idle.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // The id of the Failed report whose retry is in flight (per-row busy state), or null when idle.
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  const list = useQuery({
    ...donationsListCompanyStatsReportsOptions(),
    // The typed return is the generated `Array<StatisticsReportOut>`, but at runtime the body may be
    // the un-unwrapped `{data}` envelope — decode tolerantly so an envelope shape never breaks the
    // screen. `select` keeps the cache typed as a bare row array for the render below.
    select: (data: unknown) => extractList<StatisticsReportOut>(data),
  });

  const reports: StatisticsReportOut[] = list.data ?? [];

  // Stable so the column set that closes over it can be, which is what lets the rows skip re-render.
  const download = useCallback(
    async (report: StatisticsReportOut) => {
      if (downloadingId !== null) return;
      setDownloadingId(report.id);
      try {
        const result = await sdk.donationsDownloadStatsReport({
          ...binaryDownloadOptions,
          path: { report_id: report.id },
        });
        // The configured client returns errors in the result (it doesn't throw by default); surface a
        // mapped GivmoError / HTTP error / missing Response as a thrown error for the catch below.
        if (result.error) throw result.error;
        if (!result.response) throw new Error('Download failed: no response.');
        const meta = readBinaryMeta(result.response);
        const filename = meta.filename ?? reportFilename(report);
        // `data` is the Blob (parseAs:'blob'). Coerced through the shared helper rather than an
        // `instanceof` ternary — see `asSaveableBlob` for why that operator is unreliable here and
        // why its obvious fallback saves a file containing the text `[object Blob]`.
        blobSave(asSaveableBlob(result.data, 'application/pdf'), filename);
        toast.success('Document downloaded');
      } catch (error) {
        toast.error(
          isGivmoError(error)
            ? error.message
            : 'Could not download the document. Please try again.',
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [downloadingId],
  );

  // Retry a FAILED report by re-requesting its period (EP-FIX1 ⑤) — the same generate endpoint the
  // dialog uses; the backend enqueues a fresh attempt (the Failed row's period is not "active"). The
  // per-row `retryingId` drives the button's busy state and blocks a double-tap on the same row.
  const retryMutation = useMutation({
    ...donationsGenerateCompanyStatsReportMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: donationsListCompanyStatsReportsQueryKey(),
      });
      toast.success(
        'Report retry requested — it will be emailed to you and appear here when ready.',
      );
    },
    onError: (error) => {
      toast.error(
        isGivmoError(error) ? error.message : 'Could not retry the report. Please try again.',
      );
    },
    onSettled: () => setRetryingId(null),
  });

  const retry = useCallback(
    (report: StatisticsReportOut) => {
      if (retryingId !== null) return; // single-flight across rows.
      setRetryingId(report.id);
      retryMutation.mutate({ body: buildRetryReportBody(report) });
    },
    [retryingId, retryMutation],
  );

  // Memoized so rows skip re-render on unrelated parent state. The deps are exactly what the
  // cells close over, so a real change still rebuilds them.
  const columns: DataTableColumn<StatisticsReportOut>[] = useMemo(
    () => [
      {
        id: 'period',
        header: 'Report period',
        cell: (report) => (
          <span className="flex items-center gap-10">
            <Icon icon={FileText} size={18} className="shrink-0 text-icon" aria-hidden />
            <span
              className="truncate font-w600 text-text-strong"
              {...testIdProp(`${PREFIX}-row-${report.id}-period`)}
            >
              {reportPeriodLabel(report)}
            </span>
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '150px',
        cell: (report) => (
          <span {...testIdProp(`${PREFIX}-row-${report.id}-status`)}>
            <Badge tone={statusBadgeTone(report.status)} dot>
              {report.status}
            </Badge>
          </span>
        ),
      },
      {
        id: 'download',
        header: '',
        align: 'right',
        width: '160px',
        cell: (report) => {
          // A FAILED report offers Retry (re-request its period); everything else offers Download —
          // enabled only once COMPLETED, otherwise a disabled "Not ready" for Pending/In Progress.
          if (isReportRetryable(report)) {
            const isRetrying = retryingId === report.id;
            return (
              <div className="flex justify-end">
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  disabled={retryingId !== null}
                  loading={isRetrying}
                  leadingIcon={<Icon icon={RotateCcw} size={14} aria-hidden />}
                  onClick={() => retry(report)}
                  aria-label={`Retry generating ${reportPeriodLabel(report)}`}
                  testId={`${PREFIX}-row-${report.id}-retry`}
                >
                  Retry
                </Button>
              </div>
            );
          }
          const downloadable = isReportDownloadable(report);
          const isBusy = downloadingId === report.id;
          return (
            <div className="flex justify-end">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={!downloadable || isBusy}
                loading={isBusy}
                leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
                onClick={() => void download(report)}
                aria-label={
                  downloadable
                    ? `Download ${reportPeriodLabel(report)}`
                    : `${reportPeriodLabel(report)} is not ready to download`
                }
                title={downloadable ? undefined : 'Not ready'}
                testId={`${PREFIX}-row-${report.id}-download`}
              >
                {downloadable ? 'Download' : 'Not ready'}
              </Button>
            </div>
          );
        },
      },
    ],
    [download, downloadingId, retry, retryingId],
  );

  return (
    // The root keeps `data-testid="page-documents"` even though this is now a SECTION inside
    // /reports rather than its own page: that testid is the shipped contract every Documents
    // selector (e2e + the RTL suite) already resolves, and a testid rename is an API change.
    // `id` + `tabIndex`: the target of the Reports page's Tax documents tile (EP-WEB26), which views
    // this section in place; `scroll-mt` keeps its heading clear of the shell's sticky top bar.
    <section
      id="tax-documents"
      tabIndex={-1}
      className="flex scroll-mt-[88px] flex-col gap-16 outline-none"
      {...testIdProp('page-documents')}
    >
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="flex min-w-0 flex-col gap-4">
          <h2 className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            Tax documents
          </h2>
          <p className="font-sans text-s14 text-text-tertiary">
            Your company&rsquo;s annual donation reports. Reports become available to download once
            they finish generating.
          </p>
        </div>
        <Button
          appearance="saas"
          tone="primary"
          leadingIcon={<Icon icon={FilePlus2} size={16} aria-hidden />}
          onClick={() => setGenerateOpen(true)}
          testId={`${PREFIX}-generate-report`}
        >
          Generate report
        </Button>
      </div>

      <GenerateReportDialog open={generateOpen} onOpenChange={setGenerateOpen} />

      <DataTable
        testId={`${PREFIX}-list`}
        columns={columns}
        // Below `sm` each report reads as a stacked block — its period, then its status and its
        // Download (or Retry) on one line. As a three-column table in a scroll box, a phone showed the
        // period and status and hid the only action off-screen (EP-WEB26). Desktop is untouched: the
        // stacked reading is `max-sm:` variants only.
        stackAt="sm"
        rows={reports}
        rowKey={(r) => r.id}
        rowTestId={(r) => `${PREFIX}-row-${r.id}`}
        loading={list.isPending}
        loadingTestId={`${PREFIX}-loading`}
        error={
          list.isError
            ? isGivmoError(list.error)
              ? list.error.message
              : 'Failed to load documents.'
            : undefined
        }
        onRetry={() => void list.refetch()}
        errorTestId={`${PREFIX}-error`}
        retryTestId={`${PREFIX}-error-retry`}
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={FileText} size={20} aria-hidden />}
            title="No documents yet"
            description="Annual donation reports will appear here once they finish generating."
          />
        }
      />
    </section>
  );
}
